import type {
  InvoiceV2FilterOptionKind,
  InvoiceV2FilterOptionSuggestionsPayload,
  InvoiceV2FilterOptionSuggestionsResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import {
  zodInvoiceV2FilterOptionSuggestionsQueryParamsSchema,
  zodInvoiceV2FilterOptionSuggestionsResponseSchema,
} from './validations';

const SUGGESTIONS_ENDPOINT: Record<InvoiceV2FilterOptionKind, () => string> = {
  costCenters: ENDPOINT.INVOICE.GET_COST_CENTER_SUGGESTIONS,
  sites: ENDPOINT.INVOICE.GET_SITE_SUGGESTIONS,
};

/**
 * Fetches the cost centers or sites the caller may filter the invoice list by.
 *
 * @param payload - Which entity kind to suggest, and an optional name substring and result limit (1–50).
 * @param options - Additional request options (e.g. abort signal).
 * @returns The matching options, ordered by name.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/suggest_cost_centers_invoices_suggestions_cost_centers_get
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/suggest_sites_invoices_suggestions_sites_get
 */
export const fetchInvoiceV2FilterOptionSuggestions = async (
  { kind, queryParams = {} }: InvoiceV2FilterOptionSuggestionsPayload,
  options?: KyRequestOptions,
) => {
  return await vestigasApi
    .get(SUGGESTIONS_ENDPOINT[kind](), {
      ...options,
      searchParams: queryParams,
      validate: {
        ...options?.validate,
        response: zodInvoiceV2FilterOptionSuggestionsResponseSchema,
        searchParams:
          zodInvoiceV2FilterOptionSuggestionsQueryParamsSchema.transform(
            (data) => withoutObjectKeysWhereValueIs(data, [undefined, '']),
          ),
      },
    })
    .json<InvoiceV2FilterOptionSuggestionsResponse>();
};
